import CatalogEntity from "../Components/CatalogEntity";
import {useEffect, useState} from "react";
import {SelectFieldCellEditor, renderColumnValue, renderNonWarpingHeader, findEnumLabelByValue} from "../../../General/SharedMethods";
import useHardwareCatalog from "../Hooks/UseHardwareCatalog";
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, LinearProgress, TextField} from "@mui/material";
import {MRT_EditActionButtons} from "material-react-table";
import * as yup from 'yup';

const ActuatorEditor = ({relayOptions, powerOptions, enums, booleanOptions}) => {
    const [manufacturerOptions, setManufacturerOptions] = useState([]);
    const {getAll} = useHardwareCatalog();
    const [isLoading, setIsLoading] = useState(true);

    const ViewActuatorDialog = (columns, viewRow, setViewRow) => {
        const components = [];
        for (let i = 0; i < columns.length; i += 3) {
            components.push(
                <Grid container spacing={2} marginBottom={1} padding={1} key={`row-${i}`}>
                    {[...Array(3)].map((_, j) => {
                        const column = columns[i + j];
                        return column && (
                            <Grid item xs={12} sm={6} md={4} key={column.accessorKey}>
                                <TextField
                                    label={column.header}
                                    defaultValue={renderColumnValue(column, viewRow, enums)}
                                    InputProps={{
                                        readOnly: true,
                                    }}
                                    variant="standard"
                                    fullWidth
                                />
                            </Grid>
                        );
                    })}
                </Grid>
            );
        }

        return (
            <Dialog maxWidth="sm" open={true}>
                <DialogTitle variant="h4">View Details</DialogTitle>
                <DialogContent>
                    {components}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewRow(null)}>Close</Button>
                </DialogActions>
            </Dialog>
        );
    };

    const ActuatorDialog = (table, row, internalEditComponents, validationErrors, setValidationErrors, dialogType) => {
        const components = [];
        for (let i = 1; i < internalEditComponents.length; i += 3) {
            components.push(
                <Grid container spacing={2} marginBottom={1} padding={1} key={`row-${i}`}>
                    {[...Array(3)].map((_, j) => {
                        const component = internalEditComponents[i + j];
                        return component && (
                            <Grid item xs={12} sm={6} md={4} key={`item-${i}-${j}`}>
                                {component}
                            </Grid>
                        );
                    })}
                </Grid>
            );
        }

        return (
            <Dialog
                fullWidth
                maxWidth="sm"
                open={true}
            >
                <DialogTitle variant="h4">{dialogType === "edit" ? "Edit Actuator" : "Create a new Actuator"}</DialogTitle>
                <DialogContent
                    sx={{overflowY: 'auto'}}
                >
                    {components}
                </DialogContent>
                <DialogActions>
                    <MRT_EditActionButtons variant="text" table={table} row={row}/>
                </DialogActions>
            </Dialog>
        );
    };


    useEffect(() => {
        getAll("manufacturers")
            .then(manufacturers => {
                setManufacturerOptions(manufacturers.data.content?.map(manufacturer => ({label: manufacturer.name, value: manufacturer})));
                setIsLoading(false);
            }).catch(error => {
            setIsLoading(false);
            console.error(error);

        });
    }, []);

    const createActuatorColumns = (validationErrors, setValidationErrors) => {
        return [
            {
                accessorKey: 'id',
                header: 'Id',
                enableEditing: false,
                muiEditTextFieldProps: {
                    type: 'number',
                    hidden: true,
                },
            },
            {
                accessorKey: 'name',
                header: 'Name',
                muiEditTextFieldProps: {
                    type: 'text',
                    required: true,
                    error: !!validationErrors?.name,
                    helperText: validationErrors?.name,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            name: undefined,
                        }),
                },
            },
            {
                accessorKey: 'manufacturer',
                header: 'Manufacturer',
                filterVariant: 'multi-select',
                filterSelectOptions: manufacturerOptions.map(option => option.label),
                muiEditTextFieldProps: {
                    type: 'multi-select',
                },
                Cell: ({cell}) => cell.getValue()?.name,
                Edit: ({row, column, cell}) => SelectFieldCellEditor(
                    row,
                    column,
                    cell.getValue()?.name,
                    manufacturerOptions,
                    true,
                    setValidationErrors,
                    validationErrors)
            },
            {
                accessorKey: 'model',
                header: 'Model',
                muiEditTextFieldProps: {
                    type: 'text',
                    required: true,
                    error: !!validationErrors?.model,
                    helperText: validationErrors?.model,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            model: undefined,
                        }),
                },
            },
            {
                accessorKey: 'current',
                header: 'Current',
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.current,
                    helperText: validationErrors?.current,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            current: undefined,
                        }),
                },
            },
            {
                accessorKey: 'voltage',
                header: 'Voltage',
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.voltage,
                    helperText: validationErrors?.voltage,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            voltage: undefined,
                        }),
                },
            },
            {
                accessorKey: 'powerSupply',
                header: 'Power Supply',
                Cell: ({cell}) => findEnumLabelByValue(cell.getValue(), enums),
                editVariant: 'select',
                filterVariant: 'multi-select',
                filterSelectOptions: powerOptions.map(option => option.label),
                editSelectOptions: powerOptions,
                muiEditTextFieldProps: {
                    type: 'multi-select',
                    required: true,
                    error: !!validationErrors?.powerSupply,
                    helperText: validationErrors?.powerSupply,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            powerSupply: '',
                        }),
                },
            },
            {
                accessorKey: 'specOpenTime',
                header: 'Spec Open Time',
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.specOpenTime,
                    helperText: validationErrors?.specOpenTime,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            specOpenTime: undefined,
                        }),
                },
            },
            {
                accessorKey: 'defaultOpenTime',
                header: 'Default Open Time',
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.defaultOpenTime,
                    helperText: validationErrors?.defaultOpenTime,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            defaultOpenTime: undefined,
                        }),
                },
            },
            {
                accessorKey: 'specCloseTime',
                header: 'Spec Close Time',
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.specCloseTime,
                    helperText: validationErrors?.specCloseTime,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            specCloseTime: undefined,
                        }),
                },
            },
            {
                accessorKey: 'defaultCloseTime',
                header: 'Default Close Time',
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.defaultCloseTime,
                    helperText: validationErrors?.defaultCloseTime,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            defaultCloseTime: undefined,
                        }),
                },
            },
            {
                accessorKey: 'maxPressure',
                header: 'Max Pressure',
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.maxPressure,
                    helperText: validationErrors?.maxPressure,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            maxPressure: undefined,
                        }),
                },
            },
            {
                accessorKey: 'openIndication',
                header: 'Open Indication',
                editVariant: 'select',
                filterVariant: 'select',
                filterSelectOptions: booleanOptions,
                editSelectOptions: booleanOptions,
                Cell: ({cell}) => findEnumLabelByValue(cell.getValue(), enums),
                muiEditTextFieldProps: {
                    type: 'select',
                    required: true,
                    error: !!validationErrors?.openIndication,
                    helperText: validationErrors?.openIndication,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            openIndication: '',
                        }),
                },
            },
            {
                accessorKey: 'closeIndication',
                header: 'Close Indication',
                editVariant: 'select',
                filterVariant: 'select',
                filterSelectOptions: booleanOptions,
                editSelectOptions: booleanOptions,
                Cell: ({cell}) => findEnumLabelByValue(cell.getValue(), enums),
                muiEditTextFieldProps: {
                    type: 'select',
                    required: true,
                    error: !!validationErrors?.closeIndication,
                    helperText: validationErrors?.closeIndication,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            closeIndication: '',
                        }),
                },
            },
            {
                accessorKey: 'manualOverrideIndication',
                header: 'Manual Override Indication',
                Header: renderNonWarpingHeader('Manual Override Indication'),
                editVariant: 'select',
                filterVariant: 'select',
                filterSelectOptions: booleanOptions,
                editSelectOptions: booleanOptions,
                Cell: ({cell}) => findEnumLabelByValue(cell.getValue(), enums),
                muiEditTextFieldProps: {
                    type: 'select',
                    required: true,
                    error: !!validationErrors?.manualOverrideIndication,
                    helperText: validationErrors?.manualOverrideIndication,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            manualOverrideIndication: '',
                        }),
                },
            },
            {
                accessorKey: 'relay',
                header: 'Relay',
                Cell: ({cell}) => findEnumLabelByValue(cell.getValue(), enums),
                editVariant: 'select',
                editSelectOptions: relayOptions,
                filterVariant: 'multi-select',
                filterSelectOptions: relayOptions.map(option => option.label),
                muiEditTextFieldProps: {
                    type: 'multi-select',
                    required: true,
                    error: !!validationErrors?.relay,
                    helperText: validationErrors?.relay,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            relay: '',
                        }),
                },

            },
        ]
    }

    const actuatorValidationSchema = yup.object().shape({
        name: yup.string().required('Name is required').max(255, 'Name must be no longer than 255 characters'),
        manufacturer: yup.object().typeError('Manufacturer is required').required('Manufacturer is required'),
        model: yup.string().required('Model is required').max(255, 'Model must be no longer than 255 characters'),
        current: yup.number().typeError('Current is required').required('Current is required').min(0).max(2000),
        voltage: yup.number().typeError('Voltage is required').required('Voltage is required').min(0).max(200),
        powerSupply: yup.string().required('Power Supply is required'),
        specOpenTime: yup.number().typeError('Spec Open Time is required').required('Spec Open Time is required').min(0).max(600),
        defaultOpenTime: yup.number().typeError('Default Open Time is required').required('Default Open Time is required').min(0).max(600),
        specCloseTime: yup.number().typeError('Spec Close Time is required').required('Spec Close Time is required').min(0).max(600),
        defaultCloseTime: yup.number().typeError('Default Close Time is required').required('Default Close Time is required').min(0).max(600),
        maxPressure: yup.number().typeError('Max Pressure is required').required('Max Pressure is required').min(0).max(20),
        openIndication: yup.string().required('Open Indication is required'),
        closeIndication: yup.string().required('Close Indication is required'),
        manualOverrideIndication: yup.string().required('Manual Override Indication is required'),
        relay: yup.string().required('Relay is required'),
    });


    return isLoading ? <LinearProgress/> : <CatalogEntity entityType={'actuators'}
                                                          entityName={'Actuator'}
                                                          createColumns={createActuatorColumns}
                                                          validationSchema={actuatorValidationSchema}
                                                          customEditDialog={ActuatorDialog}
                                                          customCreateDialog={ActuatorDialog}
                                                          customViewDialog={ViewActuatorDialog}
                                                          enums={enums}

    />
}

export default ActuatorEditor;
